import {Navigate, Route, Routes, useLocation} from "react-router-dom";

// All layouts containers
// UBOLD layouts removed - using AppLayout for all app routes
import AppLayout from "@/layouts/AppLayout";
import { PATHS, PUBLIC_PATHS } from "@/routes/paths";

// Route-split pages (lazy loaded)
import { lazy, useEffect, Fragment } from "react";
const PortfolioDashboard = lazy(() => import('@/pages/dashboard/Portfolio'));
const Holdings = lazy(() => import('@/pages/portfolio/Holdings'));
const Transactions = lazy(() => import('@/pages/portfolio/Transactions'));
const Sectors = lazy(() => import('@/pages/portfolio/Sectors'));
const Performance = lazy(() => import('@/pages/portfolio/Performance'));
const Risk = lazy(() => import('@/pages/portfolio/Risk'));
const DividendCalendar = lazy(() => import('@/pages/dividends/Calendar'));
const DividendPerformance = lazy(() => import('@/pages/dividends/Performance'));
const IncomeLab = lazy(() => import('@/pages/dividends/IncomeLab'));
const IncomeAnalytics = lazy(() => import('@/pages/analytics/Income'));
const AIInsights = lazy(() => import('@/pages/analytics/AIInsights'));
const WidgetLab = lazy(() => import('@/pages/analytics/WidgetLab'));
const Reports = lazy(() => import('@/pages/analytics/Reports'));
const NotFound = lazy(() => import('@/pages/other/NotFound'));
const SettingsProfilePage = lazy(() => import('@/pages/settings/Profile'));
const SettingsPreferencesPage = lazy(() => import('@/pages/settings/Preferences'));
const SettingsSubscriptionPage = lazy(() => import('@/pages/settings/Subscription'));
const SettingsSecurityPage = lazy(() => import('@/pages/settings/Security'));
const SettingsNotificationsPage = lazy(() => import('@/pages/settings/Notifications'));
const ImportData = lazy(() => import('@/pages/setup/Import'));
const LinkBroker = lazy(() => import('@/pages/setup/LinkBroker'));

// (Public auth routes moved to src/routes/public.tsx)


import {useLayoutContext} from "@/context/useLayoutContext.tsx";
import {useAuth} from "@/context/AuthProvider";

type IRoutesProps = object

const AllRoutes = (props: IRoutesProps) => {

    const {user, isLoading} = useAuth()
    const isAuthenticated = !!user
    const {orientation} = useLayoutContext();
    const location = useLocation();
    
    // Log all route changes to track OAuth flow
    useEffect(() => {
        console.log('🛣️ [ROUTE CHANGE]:', location.pathname, location.search);
        if (location.pathname === '/auth/callback') {
            console.log('🎯 [ROUTE] HIT AUTH CALLBACK!');
        }
    }, [location]);

    // UBOLD layout system removed - AppLayout handles all app routes

    return (
        <Fragment>
            <Routes>
                {/* New App Routes with AppLayout */}
                <Route path="/app/*" element={
                    isLoading ? (
                        <div className="flex items-center justify-center min-h-screen">
                            <div className="text-center">
                                <p className="text-lg font-medium">Loading...</p>
                            </div>
                        </div>
                    ) : !isAuthenticated ? (
                        <Navigate to={PUBLIC_PATHS.auth.login} />
                    ) : (
                        <AppLayout />
                    )
                }>
                    {/* Default redirect to dashboard */}
                    <Route index element={<Navigate to="dashboard" replace />} />

                    {/* Dashboard */}
                    <Route path="dashboard" element={<PortfolioDashboard />} />

                    {/* Portfolio Routes */}
                    <Route path="portfolio">
                        <Route path="holdings" element={<Holdings />} />
                        <Route path="transactions" element={<Transactions />} />
                        <Route path="sectors" element={<Sectors />} />
                        <Route path="performance" element={<Performance />} />
                        <Route path="risk" element={<Risk />} />
                    </Route>

                    {/* Dividends Routes */}
                    <Route path="dividends">
                        <Route path="calendar" element={<DividendCalendar />} />
                        <Route path="performance" element={<DividendPerformance />} />
                        <Route path="income-lab" element={<IncomeLab />} />
                    </Route>

                    {/* Analytics Routes */}
                    <Route path="analytics">
                        <Route path="income" element={<IncomeAnalytics />} />
                        <Route path="ai-insights" element={<AIInsights />} />
                        <Route path="widget-lab" element={<WidgetLab />} />
                        <Route path="reports" element={<Reports />} />
                    </Route>

                    {/* Social Routes */}
                    <Route path="social">
                        <Route path="find-users" element={<div>Find Users - Coming Soon</div>} />
                        <Route path="following" element={<div>Following - Coming Soon</div>} />
                        <Route path="groups" element={<div>Investment Groups - Coming Soon</div>} />
                    </Route>

                    {/* Setup Routes */}
                    <Route path="setup">
                        <Route path="import" element={<ImportData />} />
                        <Route path="export" element={<div>Export Data - Coming Soon</div>} />
                        <Route path="link-broker" element={<LinkBroker />} />
                        <Route path="link-bank" element={<div>Link Bank - Coming Soon</div>} />
                        <Route path="backups" element={<div>Backups - Coming Soon</div>} />
                    </Route>

                    {/* Settings Routes */}
                    <Route path="settings">
                        <Route index element={<Navigate to="profile" replace />} />
                        <Route path="profile" element={<SettingsProfilePage />} />
                        <Route path="preferences" element={<SettingsPreferencesPage />} />
                        <Route path="subscription" element={<SettingsSubscriptionPage />} />
                        <Route path="security" element={<SettingsSecurityPage />} />
                        <Route path="notifications" element={<SettingsNotificationsPage />} />
                    </Route>

                    {/* Catch-all inside /app: show Not Found within app shell */}
                    <Route path="*" element={<NotFound />} />
                </Route>
                
                {/* Legacy UBOLD routes removed during migration */}
                
                {/* Default redirect for authenticated users */}
                <Route path="/" element={
                    isAuthenticated ? <Navigate to={PATHS.dashboard} replace /> : <Navigate to={PUBLIC_PATHS.auth.login} replace />
                } />
                
                {/* Catch-all removed here to avoid overriding public routes */}
            </Routes>
        </Fragment>
    );
};

export default AllRoutes;
